/* eslint-disable complexity */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	IconButton,
	MenuItem,
	Typography,
} from '@mui/material';
import { useRouter } from 'next/router';
import { useMutation } from '@tanstack/react-query';
import { authService, paymentService, stripeService } from '@/modules/_core/services';
import ExternalLink from '@/modules/_core/components/links/External-link';
import { Elements, ElementsConsumer, PaymentElement } from '@stripe/react-stripe-js';
import {
	calcMonthlyMembershipTierAmount,
	FileVariantsEnum,
	GetPricesAndFeesDto,
	getTierFrequencyName,
	IntakeFormUserDataResults,
	MembershipDto,
	MembershipTierDto,
	MembershipTierFrequencyEnum,
	OrderDto,
	priceFormatter,
	UserDto,
	VerificationInstance,
	waitAsync,
	IntakeFormValue,
	getStripeFutureUsage,
	commonQueryKeys,
	makeOrderPageLink,
} from '@sweatpals/shared';
import { getPublicFileUrl } from '@/modules/_core/utils/file.utils';
import { useSnackbar } from 'notistack';
import {
	clsx,
	getReponseDataFromAxiosError,
	HostBackground,
	IntakeFormResult,
	Showable,
	useStyles,
	OrderSummary,
	useDebounce,
	defaultStripeElementsOptions,
} from '@sweatpals/react-ui-kit';
import { isEmail, isPhoneNumber } from 'class-validator';
import { CheckRounded } from '@mui/icons-material';
import Image from 'next/legacy/image';
import * as yup from 'yup';
import { membershipFormStyles } from './membership-form.styles';
import RemoteImage from '@/modules/_core/components/images/Remote-image';
import { ArrowBackIcon, CheckIcon, EditIcon } from '@/modules/_core/icons';
import loader from '@/public/assets/images/loader.gif';
import { CoreTextField } from '@/modules/_core/components/_ui/core-textfield';
import { CorePhoneInput } from '@/modules/_core/components/_ui/core-phone-input';
import { CoreFormControl } from '@/modules/_core/components/_ui/core-form-control';
import { StripeElements, PaymentIntentConfirmParams } from '@stripe/stripe-js';
import { flatten, isEmpty, reduce } from 'lodash-es';
import { useFormik } from 'formik';
import { AxiosError } from 'axios';
import { isSmsEnabled, TYPEFORM_URL, HOME_PAGE_URL } from '@/modules/_core/constants';
import { CheckoutFormDto } from '@/modules/_core/interfaces/checkout-form.dto';
import { CoreSelect } from '@/modules/_core/components/_ui/core-select';
import { CoreButtonBase } from '@/modules/_core/components/_ui/core-button';
import ReCaptchaBackgroundVerifier from '@/modules/_core/components/re-captcha/re-captcha-background.component';
import { MembershipCard } from '../membership-card/membership-card.component';
import { defaultHostColorHex } from '../../pages/host/host-page.styles';
import UserAvatar from '@/modules/_core/components/images/UserAvatar';
import IntakeForm from '../../../ticket/components/intake-form/intake-form.component';
import { prepareIntakeFormValueToSave } from '@/modules/_core/utils/intake-form.utils';

enum CheckoutStepEnum {
	Form = 1,
	VerifyCode = 2,
}

export interface MembershipCheckoutFormDto {
	fullName: string;
	email: string;
	phone: string;
	code?: string;
	recaptchaTokenV2?: string;
	recaptchaTokenV3?: string;
	intakeFormResult?: IntakeFormResult;
}

export interface MembershipFormProps {
	hostData: UserDto;
	hostMemberships: MembershipDto[];
	preselectedMembershipTierId?: string;
	isMembershipSelectDisabled?: boolean;
	Header?: FC<{ clearSelectedMembershipTierId(): void }>;
	onSubmit?(order: OrderDto): Promise<void>;
}

export const MembershipForm: FC<MembershipFormProps> = (props) => {
	const {
		hostData,
		Header,
		hostMemberships,
		preselectedMembershipTierId,
		isMembershipSelectDisabled = false,
		onSubmit,
	} = props;

	const { query } = useRouter();
	const queryPromocode = query[commonQueryKeys.promocode] as string | undefined;

	const [hasMembership, setHasMemebership] = useState(false);
	const [isMembershipSoldOut, setIsMembershipSoldOut] = useState(false);
	const [checkoutStep, setCheckoutStep] = useState(CheckoutStepEnum.Form);
	const [isPolicyAgreed, setIsPolicyAgreed] = useState(true);
	const [isMembershipEditable, setIsMembershipEditable] = useState(false);
	const [resendCodeTimer, setResetCodeTimer] = useState<number | null>(null);
	const [isSignIn, setIsSignIn] = useState<boolean | null>(null);
	const [verification, setVerification] = useState<null | VerificationInstance>(null);
	const [promocode, setPromocode] = useState<string>(queryPromocode || '');
	const [promocodeError, setPromocodeError] = useState('');
	const [isPromocodeApplied, setIsPromocodeApplied] = useState(!!promocode);
	const [isPromocodeEnabled, setIsPromocodeEnabled] = useState(!!isPromocodeApplied);
	const [isPromocodeAppling, setIsPromocodeAppling] = useState(false);
	const [priceAndFees, setPriceAndFees] = useState<GetPricesAndFeesDto | null>(null);
	const [isPaying, setIsPaying] = useState(false);
	const [validateOnChange, setValidateOnChange] = useState(false);
	const [showRecaptchaCheckbox, setShowRecaptchaCheckbox] = useState(false);
	const [selectedMembershipTierId, setSelectedMembershipTierId] = useState<string | null>(preselectedMembershipTierId ?? null);

	const { debounceFunc, cancelFunc } = useDebounce(1000);
	const { enqueueSnackbar } = useSnackbar();
	const formWrapperRef = useRef<HTMLDivElement | null>(null);
	const bodyRef = useRef<HTMLDivElement | null>(null);

	const styles = useStyles(membershipFormStyles);

	const publicHostMemberships = useMemo(() => hostMemberships?.filter(m => !m.isUnlisted), [hostMemberships]);
	const primaryColor = useMemo(() => hostData?.primaryColor ?? defaultHostColorHex, [hostData?.primaryColor]);
	const selectedMembership = useMemo(
		() => selectedMembershipTierId && hostMemberships.find(membership => {
			const tierIds = membership.tiers.map(t => t.id);

			return tierIds.includes(selectedMembershipTierId);
		}),
		[hostMemberships, selectedMembershipTierId]
	);
	const selectedMembershipTier = useMemo(
		() => selectedMembershipTierId && selectedMembership?.tiers?.find(tier => tier.id === selectedMembershipTierId),
		[selectedMembership?.tiers, selectedMembershipTierId]
	);
	const getTierFrequencyDescription = useCallback((tier: MembershipTierDto) => {
		switch (tier.frequency) {
			case MembershipTierFrequencyEnum.Weekly:
				return `${priceFormatter(tier.amount)} / each week`;
			case MembershipTierFrequencyEnum.Monthly:
				return `${priceFormatter(tier.amount)} / each month`;
			case MembershipTierFrequencyEnum.Quarterly:
				return `${priceFormatter(tier.amount)} / quarterly`;
			case MembershipTierFrequencyEnum.SemiAnnual:
				return `${priceFormatter(tier.amount)} / two times a year`;
			case MembershipTierFrequencyEnum.Annual:
				return `${priceFormatter(tier.amount)} / yearly`;
			case MembershipTierFrequencyEnum.OneTime:
				return priceFormatter(tier.amount);
		}
	}, []);

	const intakeFormFields = useMemo(() => [
		...flatten((selectedMembership?.intakeFormSettings?.sections ?? []).map(section => section.fields)),
	], [selectedMembership]);

	const prepareIntakeFormResultToSave = useCallback(
		(results: IntakeFormResult): IntakeFormUserDataResults =>
			reduce(results, (acc, value, key) => {
				const field = intakeFormFields.find(f => f.name === key);

				return { ...acc, [key]: prepareIntakeFormValueToSave(value, field) };
			}, {}),
		[intakeFormFields]
	);

	const {
		isLoading: isPaymentIntentLoading,
		mutateAsync: createPaymentIntent,
	} = useMutation(
		async ({ intakeFormResult, ...data }: MembershipCheckoutFormDto) => {
			if (!selectedMembershipTierId) {
				return;
			}

			return paymentService.createPublicPaymentIntentV2({
				...data,
				membershipTierId: selectedMembershipTierId,
				code: checkoutStep === CheckoutStepEnum.VerifyCode ? data.code : undefined,
				applyPromocode: promocode,
				intakeFormResults: intakeFormResult && prepareIntakeFormResultToSave(intakeFormResult),
			})
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				if (data.statusCode === 409) {
					if (data.message.includes('There are not enough spots')) {
						setIsMembershipSoldOut(true);
					} else {
						setHasMemebership(true);
					}

					return;
				}

				if (err instanceof AxiosError && err.response?.status === 403) {
					setShowRecaptchaCheckbox(err.response?.status === 403);
				}

				const msg = data?.message || err.message;
				enqueueSnackbar(msg, { variant: 'error' });
			},
		}
	);

	const {
		data: newPriceAndFees,
		isLoading: isPriceAndFeesLoading,
		mutateAsync: getPriceAndFees,
	} = useMutation(
		async (data: { membershipTierId: string, applyPromocode?: string }) => {
			const priceAndFees = await paymentService.getPublicPrices(data);

			setIsPromocodeApplied(!!priceAndFees.discountIds?.length);

			return priceAndFees;
		},
		{
			onError: (err: Error) => {
				const data = getReponseDataFromAxiosError(err);

				enqueueSnackbar(data?.message || err.message, { variant: 'error' });
			},
		}
	);

	useEffect(() => {
		if (newPriceAndFees && !isPriceAndFeesLoading) {
			setPriceAndFees(newPriceAndFees);
		}
	}, [newPriceAndFees, isPriceAndFeesLoading]);

	useEffect(() => {
		if (selectedMembershipTierId) {
			getPriceAndFees({ membershipTierId: selectedMembershipTierId, applyPromocode: queryPromocode });
		}
	}, []);

	const onSendVerificationCode = useCallback(async (phone: string) => {
		try {
			const { verification, isSignIn } = await authService.signInOrSignUpWithPhone(phone);

			setVerification(verification);
			setIsSignIn(isSignIn);
			setResetCodeTimer(60);
		} catch (e) {
			if (e instanceof AxiosError) {
				enqueueSnackbar(e.response?.data?.message || e.message);
			}
		}
	}, [enqueueSnackbar]);

	const getOrder = useCallback(async (orderId: string): Promise<OrderDto | undefined> => {
		try {
			let order: OrderDto | undefined;
			let attempts = 0;

			while (!order && attempts <= 10) {
				attempts++;
				await waitAsync(5000);
				order = await paymentService.getOrderByIdOrPaymentIntentId(orderId);
			}

			return order;
		} catch (e) {
			if (e instanceof AxiosError) {
				enqueueSnackbar(e.response?.data?.message || e.message, { variant: 'error' });
			}
		}
	}, [enqueueSnackbar]);

	const onFreeSubmit = useCallback(async (values: CheckoutFormDto): Promise<void> => {
		if (checkoutStep === CheckoutStepEnum.Form) {
			await onSendVerificationCode(values.phone);
			setCheckoutStep(CheckoutStepEnum.VerifyCode);
			formWrapperRef.current?.scrollTo(0, 0);
			bodyRef.current?.scrollTo(0, 0);

			return;
		}

		if ((!verification && isSmsEnabled) || isSignIn === null) {
			return;
		}

		const paymentIntent = await createPaymentIntent(values);
		const order = await getOrder(paymentIntent.orderId);

		if (order) {
			await onSubmit?.(order);
			setSelectedMembershipTierId(null);
		}
	}, [
		checkoutStep,
		verification,
		isSignIn,
		createPaymentIntent,
		getOrder,
		onSendVerificationCode,
		onSubmit,
	]);

	const onEditMembershipButtonClick = useCallback((): void => {
		if (isMembershipEditable) {
			cancelFunc();
			getPriceAndFees({
				membershipTierId: selectedMembershipTierId,
				applyPromocode: isPromocodeApplied ? promocode : undefined
			});
		}

		setIsMembershipEditable(!isMembershipEditable);
	}, [cancelFunc, getPriceAndFees, isPromocodeApplied, isMembershipEditable, promocode, selectedMembershipTierId]);

	const validationSchema = useMemo(() => {
		const validationSchema: Record<string, yup.Schema> = {
			fullName: yup.string().required('First name is required')
				.min(3, 'Full name is too short'),
			email: yup.string().required('Email is required')
				.test({
					message: 'Invalid email',
					test: value => isEmail(value),
				}),
			phone: yup.string().required('Phone number is required')
				.test({
					message: 'Invalid phone number',
					test: value => isPhoneNumber(value, null),
				}),
		};

		if (intakeFormFields.length && selectedMembership) {
			validationSchema.intakeFormResult = yup.object().shape(
				intakeFormFields.reduce((acc, field) => {
					let validation = field.type === 'check-box'
						? yup.boolean()
						: field.type === 'checkbox-list'
							? yup.array()
							: field.type === 'date-picker'
								? yup.date() : yup.string();

					if (field.required) {
						validation = validation.required('This field is required');

						if (validation instanceof yup.BooleanSchema) {
							validation = validation.oneOf([true], 'This field must be checked');
						}
					} else {
						validation = validation.optional();
					}

					if (field.type === 'text-input' && validation instanceof yup.StringSchema) {
						if (field.textInputType === 'email') {
							validation = validation.test({
								message: 'Invalid email',
								test: value => isEmail(value),
							});
						}

						if (field.textInputType === 'phone-number') {
							validation = validation.test({
								message: 'Invalid phone number',
								test: value => (!field.required && !value) || isPhoneNumber(value, null),
							});
						}

						if (field.validateIsEqualToField) {
							validation = validation.oneOf([yup.ref(field.validateIsEqualToField), ''], 'Incorrect value');
						}
					}

					return { ...acc, [field.name]: validation };
				}, {})
			);
		}

		return yup.object().shape(validationSchema);
	}, [intakeFormFields, selectedMembership]);

	const {
		values,
		errors,
		handleChange,
		setFieldValue,
		isSubmitting,
		validateField,
		validateForm,
		handleSubmit,
		setValues,
	} = useFormik<MembershipCheckoutFormDto>({
		initialValues: {
			fullName: '',
			email: '',
			phone: '+1',
			recaptchaTokenV2: undefined,
			recaptchaTokenV3: undefined,
		},
		validateOnChange,
		onSubmit: onFreeSubmit,
		validationSchema,
	});

	const handleChangeIntakeFormValues = useCallback((key: string, value?: IntakeFormValue) => {
		setValues({
			...values,
			intakeFormResult: {
				...values.intakeFormResult,
				[key]: value,
			}
		});
	}, [setValues, values]);

	const handleSetRecaptchaV2Token = useCallback(
		(token: string | null) => {
			setFieldValue('recaptchaTokenV2', token);
			setFieldValue('recaptchaTokenV3', null);
		},
		[setFieldValue]
	);

	const handleSetRecaptchaV3Token = useCallback(
		(token: string | null) => {
			setFieldValue('recaptchaTokenV3', token);
			setFieldValue('recaptchaTokenV2', null);
		},
		[setFieldValue]
	);

	const handleChangePhone = useCallback((phone: string): void => {
		setFieldValue('phone', phone);
	}, [setFieldValue]);

	const applyPromocode = useCallback(async () => {
		setIsPromocodeAppling(true);
		setPromocodeError('');

		try {
			cancelFunc();
			const pricesAndFees = await getPriceAndFees({
				membershipTierId: selectedMembershipTierId,
				applyPromocode: promocode
			});

			if (!pricesAndFees.discountIds?.length) {
				setPromocodeError('Promocode not found');
			}
		} finally {
			setIsPromocodeAppling(false);
		}
	}, [cancelFunc, getPriceAndFees, promocode, selectedMembershipTierId]);

	const onBackButtonClick = useCallback(() => {
		setVerification(null);
		setFieldValue('code', '');
		setCheckoutStep(CheckoutStepEnum.Form);
		formWrapperRef.current?.scrollTo(0, 0);
		bodyRef.current?.scrollTo(0, 0);
	}, [setFieldValue]);

	const handlePay = useCallback(async (elements: StripeElements): Promise<void> => {
		setIsPaying(true);

		try {
			const errors = await validateForm();

			if (!isEmpty(errors)) {
				return;
			}

			setValidateOnChange(false);
			const paymentIntent = await createPaymentIntent(values);

			if (paymentIntent.intent) {
				const confirmParams: PaymentIntentConfirmParams = {
					return_url: paymentIntent.orderId ? `${HOME_PAGE_URL}/${makeOrderPageLink({ orderId: paymentIntent.orderId })}` : undefined,
				};
				const response = await stripeService.confirmCardPayment(elements, paymentIntent.intent.paymentIntent, confirmParams);

				if (response.error) {
					enqueueSnackbar(response.error.message, { variant: 'error' });

					return;
				}

				if (response.paymentIntent.status === 'succeeded') {
					const order = await getOrder(paymentIntent.orderId);

					if (order) {
						await onSubmit?.(order);
						setSelectedMembershipTierId(null);
					}
				}
			}
		} finally {
			setIsPaying(false);
		}
	}, [createPaymentIntent, enqueueSnackbar, getOrder, onSubmit, validateForm, values]);

	return (
		<Box sx={styles.root}>
			<HostBackground
				background={
					hostData?.backgroundId &&
					getPublicFileUrl(hostData.backgroundId, {
						variant: FileVariantsEnum.L,
					})
				}
				primaryColor={hostData.primaryColor}
			/>

			<Box sx={styles.content}>
				{Header && (
					<Header
						clearSelectedMembershipTierId={() => {
							setSelectedMembershipTierId(null);
						}}
					/>
				)}

				<Showable show={hasMembership || isMembershipSoldOut}>
					<Box display="flex" flex={1} justifyContent="center" alignItems="center" overflow="hidden">
						<Box sx={styles.placeholder}>
							<RemoteImage
								src={hostData.avatarId}
								alt="host avatar"
								width={50}
								height={50}
								sx={{ borderRadius: '50%', margin: '0 auto' }}
							/>
							<Box display="flex" flexDirection="column" gap="32px">
								<Typography sx={styles.placeholderTitle}>
									{isMembershipSoldOut
										? `Sorry, this membership is fully sold out`
										: `There's already a membership associated with this number`
									}
								</Typography>
								<Typography sx={styles.placeholderDescription}>
									{isMembershipSoldOut
										? `All spots have been taken, and it’s no longer available for purchase`
										: (`To cancel your membership, please email us at hello@sweatpals.com.`
											+ ` After cancellation, you will still be able to use your membership until its expiration date,`
											+ ` but no further charges will be applied. We typically answer within 24h.`)
									}
								</Typography>
								<Box
									display="flex"
									justifyContent="center"
									alignItems="center"
									flexDirection={{ xs: 'column', md: 'row' }}
									gap="25px"
								>
									<Showable show={hasMembership}>
										<CoreButtonBase
											variant="secondary"
											sx={{ width: 'fit-content' }}
											onClick={() => {
												onBackButtonClick();
												setHasMemebership(false);
											}}
										>
											try a different number
										</CoreButtonBase>
									</Showable>
									<ExternalLink href={TYPEFORM_URL}>
										<CoreButtonBase variant="primary" sx={{ width: 'fit-content' }}>
											Contact support
										</CoreButtonBase>
									</ExternalLink>
								</Box>
							</Box>
						</Box>
					</Box>
				</Showable>

				<Showable hide={hasMembership || isMembershipSoldOut}>
					{selectedMembershipTierId ? (
						<Box sx={styles.body} ref={bodyRef}>
							<Box sx={styles.membershipFormLeftPart}>
								<Box p="24px" display="flex" alignItems="center" gap="12px">
									<RemoteImage
										src={hostData.avatarId}
										alt="host avatar"
										width={50}
										height={50}
										sx={{ borderRadius: '50%' }}
									/>

									<Typography sx={styles.membershipInfoHost}>{hostData.fullName}</Typography>
								</Box>
								<Box position="relative" zIndex={1}>
									<svg
										style={{
											height: '1px',
											width: '100%',
											position: 'absolute',
											bottom: 0,
											left: 0,
											strokeWidth: '1px',
										}}
									>
										<line x1="0" y1="0" x2="100%" y2="0" stroke="#FFFFFF1A" strokeDasharray="6 6" />
									</svg>
									<Button
										sx={styles.editMembershipButton}
										disabled={isPriceAndFeesLoading && !isMembershipEditable}
										onClick={async (): Promise<void> => {
											await onEditMembershipButtonClick();
										}}
									>
										{isMembershipEditable ? <CheckIcon /> : <EditIcon />}
										{isMembershipEditable ? 'Save' : 'Edit'}
									</Button>
								</Box>
								<Box sx={styles.orderSummary}>
									<Showable show={isPriceAndFeesLoading && !isMembershipEditable}>
										<Box sx={styles.loader}>
											<Image src={loader} width={64} height={64} alt="order-louder" />
										</Box>
									</Showable>
									<Box
										sx={{
											height: { xs: 'auto', md: '100%' },
											overflow: { xs: 'unset', md: 'auto' },
											p: '0 12px 1px',
											opacity: isPriceAndFeesLoading && !isMembershipEditable ? 0.2 : 1,
										}}
									>
										{isMembershipEditable ? (
											<Box display="flex" flexDirection="column" gap="8px">
												{selectedMembershipTier ? (
													<Box display="flex" alignItems="center" justifyContent="space-between">
														<Box display="flex" alignItems="flex-start" gap="4px">
															<Typography sx={styles.membershipTierAmount}>
																{priceFormatter(selectedMembershipTier.amount)}
															</Typography>
															<CoreSelect
																value={selectedMembershipTierId}
																renderValue={() =>
																	selectedMembershipTier ? getTierFrequencyName(selectedMembershipTier.frequency) : ''
																}
																onChange={(e) => {
																	setSelectedMembershipTierId(e.target.value);
																}}
																MenuProps={{
																	anchorOrigin: {
																		vertical: 'bottom',
																		horizontal: 'left',
																	},
																	transformOrigin: {
																		vertical: 'top',
																		horizontal: 'left',
																	},
																}}
																paperSx={{
																	'& .MuiList-root': {
																		minWidth: '284px',

																		'& .MuiMenuItem-root': {
																			justifyContent: 'space-between',
																			backgroundColor: '#00000080',
																			gap: '10px',
																			p: '8px 12px',
																		},
																	},
																}}
																sx={styles.membershipTierSelect}
															>
																{selectedMembership?.tiers?.map(tier => (
																	<MenuItem key={tier.id} value={tier.id}>
																		<Box display="flex" flexDirection="column">
																			<Typography sx={styles.membershipTierSelectMenuItemText}>
																				{getTierFrequencyName(tier.frequency)}
																			</Typography>
																			<Typography
																				sx={clsx(styles.membershipTierSelectMenuItemText, {
																					color: '#FFFFFFCC',
																				})}
																			>
																				{getTierFrequencyDescription(tier)}
																			</Typography>
																		</Box>
																		<Box display="flex" alignItems="center" gap="12px">
																			<Showable hide={tier.frequency === MembershipTierFrequencyEnum.OneTime}>
																				<Typography
																					sx={clsx(styles.membershipTierSelectMenuItemText, {
																						color: '#E1E1E1',
																						lineHeight: '100%',
																					})}
																				>
																					{priceFormatter(calcMonthlyMembershipTierAmount(tier))} / mo
																				</Typography>
																			</Showable>
																			<Showable show={tier.id === selectedMembershipTierId}>
																				<CheckIcon />
																			</Showable>
																		</Box>
																	</MenuItem>
																))}
															</CoreSelect>
														</Box>
														<Showable hide={selectedMembershipTier.frequency === MembershipTierFrequencyEnum.OneTime}>
															<Typography sx={styles.membershipMonthlyFrequency}>
																{priceFormatter(calcMonthlyMembershipTierAmount(selectedMembershipTier))} / month
															</Typography>
														</Showable>
													</Box>
												) : null}
												<CoreSelect
													disabled={isMembershipSelectDisabled}
													value={selectedMembership?.id}
													renderValue={() => selectedMembership?.name}
													onChange={(e) => {
														const membershipId = e.target.value;
														const membership = hostMemberships.find((m) => m.id === membershipId);
														const tier = membership?.tiers?.[0];

														if (tier) {
															setSelectedMembershipTierId(tier.id);
														}
													}}
													paperSx={{
														'& .MuiList-root': {
															'& .MuiMenuItem-root': {
																justifyContent: 'space-between',
															},
														},
													}}
													sx={{
														'& .MuiSelect-select': {
															p: '10px 16px',
														},

														'& .MuiSelect-icon': {
															display: isMembershipSelectDisabled ? 'none' : 'block',
														}
													}}
												>
													{publicHostMemberships.map((membership) => (
														<MenuItem key={membership.id} value={membership.id}>
															<Typography variant="inherit">{membership.name}</Typography>
															<Showable show={membership.id === selectedMembership?.id}>
																<CheckIcon />
															</Showable>
														</MenuItem>
													))}
												</CoreSelect>
												<Typography sx={styles.membershipDescription}>{selectedMembership?.description}</Typography>
											</Box>
										) : (
											<OrderSummary
												promocode={promocode}
												membership={selectedMembership}
												selectedMembershipTierId={selectedMembershipTierId}
												isPromocodeApplied={isPromocodeApplied}
												priceAndFees={priceAndFees}
											/>
										)}
									</Box>
								</Box>
							</Box>

							<Box sx={styles.membershipFormRightPart} ref={formWrapperRef}>
								<Box sx={styles.form} component="form" onSubmit={handleSubmit}>
									<Showable show={checkoutStep === CheckoutStepEnum.VerifyCode}>
										<IconButton sx={styles.backButton} disabled={isSubmitting} onClick={onBackButtonClick}>
											<ArrowBackIcon />
										</IconButton>
									</Showable>
									<Showable show={checkoutStep === CheckoutStepEnum.Form}>
										<Box display="flex" flexDirection="column" gap="24px">
											<Typography sx={styles.title}>Your info</Typography>
											<CoreTextField
												controlId="fullName"
												id="fullName-input"
												value={values.fullName}
												name="fullName"
												label="Full name"
												placeholder="Full name"
												requiredMark
												disabled={isSubmitting || isPaying}
												onChange={handleChange}
												onBlur={() => {
													validateField('fullName');
												}}
												error={!!errors.fullName}
												helperText={errors.fullName}
											/>
											<CoreTextField
												controlId="email"
												id="email-input"
												value={values.email}
												name="email"
												label="Email"
												placeholder="Enter email"
												requiredMark
												disabled={isSubmitting || isPaying}
												onChange={handleChange}
												onBlur={() => {
													validateField('email');
												}}
												error={!!errors.email}
												helperText={errors.email}
											/>
											<CorePhoneInput
												controlId="phone"
												id="phone-input"
												placeholder="Phone Number"
												label="Phone Number"
												requiredMark
												name="phone"
												disabled={isSubmitting || isPaying}
												value={values.phone}
												defaultCountry="US"
												forceCallingCode
												MenuProps={{ disableAutoFocusItem: true }}
												onChange={handleChangePhone}
												onBlur={() => {
													validateField('phone');
												}}
												error={!!errors.phone}
												helperText={errors.phone}
											/>
											<Showable show={selectedMembership?.intakeFormSettings}>
												<IntakeForm
													disabled={isSubmitting || isPaying}
													intakeFormSettings={selectedMembership?.intakeFormSettings}
													intakeFormValues={values.intakeFormResult}
													intakeFormErrors={errors.intakeFormResult}
													changeIntakeFormValues={handleChangeIntakeFormValues}
													onBlur={e => {
														if (e.target.name) {
															validateField(`intakeFormResult.["${e.target.name}"]`);
														}
													}}
												/>
											</Showable>
											<Box display="flex" alignItems="center" justifyContent="space-between">
												<Typography sx={styles.title}>Payment</Typography>
												<Showable show={!isPromocodeEnabled}>
													<Button
														sx={styles.secondaryButton}
														disabled={isSubmitting || isPaying}
														onClick={() => {
															setIsPromocodeEnabled(true);
														}}
													>
														Apply promocode
													</Button>
												</Showable>
											</Box>
											<Showable show={isPromocodeEnabled}>
												<CoreFormControl label="Promo Code">
													<Box display="flex" alignItems="flex-start" gap="13px">
														<CoreTextField
															value={promocode}
															placeholder="Enter promo code"
															sx={{
																'& .MuiInputBase-input': {
																	textTransform: 'uppercase',
																},
															}}
															controlSx={{ flex: 1 }}
															disabled={isSubmitting || isPromocodeApplied || isPromocodeAppling || isPaying}
															onChange={(e) => {
																setPromocode(e.target.value?.toLowerCase());
																setPromocodeError('');
															}}
															error={!!promocodeError}
															helperText={promocodeError}
														/>
														{isPromocodeApplied ? (
															<Box sx={styles.appliedPromocode}>
																<CheckIcon />
																Applied
															</Box>
														) : (
															<Button
																sx={styles.applyPromocodeButton}
																disabled={isSubmitting || isPaying || isPromocodeAppling}
																onClick={applyPromocode}
															>
																Apply
															</Button>
														)}
													</Box>
												</CoreFormControl>
											</Showable>
											<Showable show={(priceAndFees?.fullPrice ?? 0) > 0}>
												<Box position="relative">
													<Elements
														stripe={stripeService.stripe}
														options={{
															...defaultStripeElementsOptions({
																currency: priceAndFees?.currency,
																amount: priceAndFees?.fullPrice ?? 50,
																onBehalfOf: selectedMembership?.mainCommunity?.stripeConnectedAccountId,
																paymentMethodTypes: ['card'],
																setupFutureUsage: getStripeFutureUsage({ membership: true }),
															}),

															appearance: {
																theme: 'night',
																variables: {
																	colorText: '#FFFFFF',
																	colorTextPlaceholder: '#FFFFFF99',
																	colorTextSecondary: '#C6C6C6',
																	colorIconRedirect: '#656565',
																	colorIconTab: '#FFFFFF',
																	colorIconTabHover: '#FFFFFF',
																	colorIconTabSelected: '#FFFFFF',
																},
																rules: {
																	'.Label': {
																		fontSize: '15px',
																		marginBottom: '8px',
																	},
																	'.Input': {
																		// SWTP-1350
																		// This is a bug in all browsers when using native select input with transparent background - leeds to white background of dropdown options
																		// https://codepen.io/AmeliaBR/pres/PpEPGz
																		// But in stripe we dont have an css selector only for dropdown options (e.g. `.Input option`), so using `.Input` selector
																		// To fix this we need to set it to solid color, in order to propagate background to dropdown options
																		backgroundColor: '#1D1D1D', // same as '#FFFFFF0D',
																		borderWidth: '0px',
																		boxShadow: 'none',
																	},
																	'.Input:focus': {
																		boxShadow: '0 0 0 2px #2B2D2F',
																	},
																	'.Tab': {
																		backgroundColor: '#FFFFFF0D',
																		color: '#FFFFFF',
																		fontSize: '13px',
																		fontWeight: '500',
																		lineHeight: '16px',
																		border: '2px solid #FFFFFF0D',
																		boxShadow: '0px 2px 4px 0px #00000012',
																	},
																	'.Tab:hover': {
																		borderColor: '#FFFFFF',
																		boxShadow: '0px 2px 4px 0px #00000012',
																	},
																	'.Tab:focus': {
																		borderColor: '#FFFFFF',
																		boxShadow: '0px 2px 4px 0px #00000012',
																	},
																	'.Tab--selected': {
																		color: '#FFFFFF',
																		borderColor: '#FFFFFF',
																		boxShadow: '0px 2px 4px 0px #00000012',
																	},
																	'.Tab--selected:hover': {
																		color: '#FFFFFF',
																		borderColor: '#FFFFFF',
																		boxShadow: '0px 2px 4px 0px #00000012',
																	},
																	'.Tab--selected:focus': {
																		color: '#FFFFFF',
																		borderColor: '#FFFFFF',
																		boxShadow: '0px 2px 4px 0px #00000012',
																	},
																	'.Block': {
																		backgroundColor: '#00000033',
																		borderRadius: '4px',
																		padding: '12px',
																		fontSize: '15px',
																		fontWeight: '600',
																		lineHeight: '21px',
																		borderWidth: '0px',
																	},
																	'.c-BlockDivider--horizontal': {
																		marginTop: '12px',
																		marginBottom: '10px',
																		backgroundColor: '#FFFFFF0D',
																	},
																	'.RedirectText': {
																		fontSize: '13px',
																		lineHeight: '16px',
																	},
																},
															},
														}}
													>
														<ElementsConsumer>
															{({ elements }) => (
																<Box
																	sx={{
																		display: 'flex',
																		flexDirection: 'column',
																		gap: '24px',
																		opacity: isPaying ? 0.2 : 1,
																	}}
																>
																	<PaymentElement
																		id="payment-element"
																		options={{
																			layout: 'tabs',
																		}}
																	/>
																	<Box sx={{ display: 'flex', justifyContent: 'center' }}>
																		<Button
																			sx={styles.submitButton}
																			disabled={!isPolicyAgreed}
																			onClick={() => {
																				handlePay(elements);
																			}}
																		>
																			Pay with card
																		</Button>
																	</Box>
																</Box>
															)}
														</ElementsConsumer>
													</Elements>
													<Showable show={isPaying}>
														<Box sx={styles.loader}>
															<Image src={loader} width={64} height={64} alt="stripe-louder" />
														</Box>
													</Showable>
												</Box>
											</Showable>

											<Box>
												<ReCaptchaBackgroundVerifier
													showCheckbox={showRecaptchaCheckbox}
													onVerifyV2={handleSetRecaptchaV2Token}
													onVerifyV3={handleSetRecaptchaV3Token}
												/>
											</Box>
										</Box>
									</Showable>
									<Showable show={checkoutStep === CheckoutStepEnum.VerifyCode}>
										<Box display="flex" flexDirection="column" gap="8px" mb="24px">
											<Typography sx={styles.verificationTitle}>Confirm phone number</Typography>
											<Typography sx={styles.verificationDescription}>
												{`Enter code that we have sent you on `}
												<Box component="span" fontWeight={600}>
													{values.phone}
												</Box>
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
											<CoreTextField
												fullWidth
												placeholder="Code"
												disabled={isSubmitting}
												value={values.code}
												name="code"
												autoComplete="one-time-code"
												onChange={handleChange}
												error={!!errors.code}
												helperText={errors.code}
											/>
											<Typography
												sx={{
													fontFamily: 'Montserrat',
													fontStyle: 'normal',
													fontWeight: 400,
													fontSize: '13px',
													lineHeight: '16px',
													color: '#FFFFFF',
												}}
											>
												{resendCodeTimer ? (
													<Box component="span">Resend in {resendCodeTimer}.</Box>
												) : (
													<Box
														component="span"
														sx={
															!isSubmitting
																? {
																		'&:hover': {
																			cursor: 'pointer',
																			textDecoration: 'underline',
																		},
																	}
																: undefined
														}
														onClick={() => {
															if (!isSubmitting) {
																onSendVerificationCode(values.phone);
															}
														}}
													>
														Resend code
													</Box>
												)}
											</Typography>
										</Box>
									</Showable>
									<Showable hide={(priceAndFees?.fullPrice ?? 0) > 0}>
										<Box sx={{ display: 'flex', justifyContent: 'center', mt: '24px' }}>
											<Button
												sx={styles.submitButton}
												disabled={!isPolicyAgreed || isPaymentIntentLoading || isSubmitting}
												type="submit"
											>
												{isSubmitting ? (
													<CircularProgress />
												) : checkoutStep === CheckoutStepEnum.Form ? (
													'Get Membership'
												) : (
													'Continue'
												)}
											</Button>
										</Box>
									</Showable>
								</Box>
								<Showable show={checkoutStep === CheckoutStepEnum.Form}>
									<Box sx={{ mt: '16px', display: 'flex', justifyContent: 'center', px: '10px' }}>
										<FormControlLabel
											sx={styles.checkBox}
											control={
												<Checkbox
													id="privacy-policy"
													disabled={isSubmitting || isPaying}
													checked={isPolicyAgreed}
													onChange={() => {
														setIsPolicyAgreed(!isPolicyAgreed);
													}}
													checkedIcon={<CheckRounded />}
												/>
											}
											label={
												<>
													By continuing, you agree to our{' '}
													<a href="/privacy-policy.html" target="_blank">
														Privacy Policy
													</a>{' '}
													and{' '}
													<a href="/terms.html" target="_blank">
														Terms & Conditions
													</a>
												</>
											}
										/>
									</Box>
								</Showable>
							</Box>
						</Box>
					) : (
						<Box sx={styles.selectMembership}>
							<Box
								sx={clsx(styles.selectMembershipBody, {
									width: '100%',
									maxWidth: publicHostMemberships?.length === 1 ? '634px' : 'unset'
								})}
							>
								<Box display="flex" flexDirection="column" gap="16px">
									<UserAvatar user={hostData} sx={{ width: '49px', height: '49px' }} />
									<Typography sx={styles.selectMembershipTitle}>
										{`Explore ${hostData.fullName}’s Membership Plans`}
									</Typography>
								</Box>
								<Box sx={styles.selectMembershipContent}>
									<Box sx={styles.membershipsCards}>
										{publicHostMemberships
											.slice()
											.sort(m => (m.isPinned ? -1 : 0))
											.map((membership, _idx) => (
												<MembershipCard
													key={membership.id}
													hostPrimaryColorHex={primaryColor}
													membership={membership}
													drawBackground={membership.isPinned}
													onGetMembership={(membershipTierId) => {
														setSelectedMembershipTierId(membershipTierId);
														cancelFunc();
														getPriceAndFees({
															membershipTierId,
															applyPromocode: isPromocodeApplied ? promocode : undefined
														});
													}}
													sx={{
														width: publicHostMemberships?.length === 1 ? '100%' : '380px',
														maxWidth: 'unset',
														height: 'fit-content',
													}}
												/>
											))}
									</Box>
									<Typography sx={styles.selectMembershipNote}>
										{`Your member’s only discount will be automatically applied to your ticket purchase with ${hostData.fullName}`}
									</Typography>
								</Box>
							</Box>
						</Box>
					)}
				</Showable>
			</Box>
		</Box>
	);
};
